<template>
  <div class="products">
    <div class="products__top-banner container background-container">
      <p class="font-weight-bold">Whatever your interest you’ll</p>
      <h1>
        Find a book that’s <br class="products__container-br" />
        <span class="products__title-background">
          <span class="px-2">right for you</span>
        </span>
      </h1>
      <ShareButton />
    </div>

    <div class="page container mt-5">
      <Carousel
        id="trendingProducts"
        class="my-4"
        :items="trendingProducts"
        :mode="CarouselModes.PRODUCT"
        show-default-item-actions
        @click="handleProductClick"
      >
        <template #title>
          <h2>Trending books</h2>
        </template>
      </Carousel>

      <Carousel
        id="newProducts"
        class="mt-5"
        :items="newProducts"
        :mode="CarouselModes.PRODUCT"
        show-default-item-actions
        @click="handleProductClick"
      >
        <template #title>
          <h2>New in books</h2>
        </template>
      </Carousel>

      <PreviewItems
        class="mt-5"
        :items="products"
        :total="productsTotal"
        :loading="newProductsLoading"
        :filter-value="filterValue"
        :filtered-values-loading="filteredProductsLoading"
        :is-nothing-found="isNothingFound"
        not-found-message="Books not found"
        title="All books"
        type="products"
        @click="handleProductClick"
        @onViewMoreClick="getNewProducts"
        @onFilterValueChange="handleFilterValueChange"
      />
    </div>
  </div>
</template>

<script>
import { get, isEmpty } from 'lodash';
import { mapGetters } from 'vuex';
import {
  DefaultFilterOptions,
  DefaultPageLimit,
  CarouselModes,
  ProductFormatAvailability
} from '@/misc/constants';

import ShareButton from '@/components/Elements/ShareButton';
import PreviewItems from '@/components/PreviewItems/Index';

import { fetchProducts } from '@/services/products';
import {
  fetchProductsTrending,
  fetchProductsLatest
} from '@/services/statistics';
import formatMoney from '@/misc/formatMoney';
import filterProductsByRegion from '@/misc/filterProductByRegion';
import getDefaultProductFormat from '@/misc/getDefaultProductFormat';
import getFormatPrice from '@/misc/getFormatPrice';

export default {
  name: 'AllProducts',

  components: {
    ShareButton,
    PreviewItems,
    Carousel: () =>
      import(/* webpackPrefetch: true */ '@/components/Carousel/Index')
  },

  asyncData({ $axios, error, store }) {
    return Promise.all([
      fetchProductsTrending($axios),
      fetchProducts($axios, {
        query: {
          $sort: { createdAt: -1 },
          $limit: DefaultPageLimit
        }
      }),
      fetchProductsLatest($axios)
    ])
      .then((values) => {
        return {
          products: filterProductsByRegion(
            get(values, '[1].data'),
            store.getters['config/region']
          ),
          productsTotal: get(values, '[1].total'),
          trendingProducts: filterProductsByRegion(
            get(values, '[0].data'),
            store.getters['config/region']
          ),
          newProducts: filterProductsByRegion(
            get(values, '[2].data'),
            store.getters['config/region']
          )
        };
      })
      .catch(() => {
        error({ statusCode: 500 });
      });
  },

  data() {
    return {
      CarouselModes,
      newProductsLoading: false,
      filteredProductsLoading: false,
      isNothingFound: false,
      filterValue: DefaultFilterOptions[0].value
    };
  },
  head: {
    title: 'Discover our books | Found',
    meta: [
      {
        hid: 'description',
        name: 'description',
        content:
          'Found it! - Discover hundreds of book titles, from top social creators, to tickle your tastebuds with their recipes'
      }
    ]
  },

  computed: {
    ...mapGetters({
      currency: 'config/currency',
      region: 'config/region'
    })
  },

  methods: {
    handleProductClick(params) {
      this.$router.push({
        path: `products/${
          this.$get(params, 'item.id') || this.$get(params, 'id')
        }`
      });
    },
    getProductFormatPrice(product) {
      const defaultFormat = getDefaultProductFormat(product);
      return formatMoney(
        getFormatPrice(defaultFormat, this.region)?.amount,
        this.currency
      );
    },
    getNewProducts() {
      this.newProductsLoading = true;
      const query = {
        $where: {
          formats: {
            $elemMatch: {
              'prices.region': this.region,
              deletedAt: { $exists: false },
              availability: ProductFormatAvailability.Available
            }
          }
        },
        $sort: { createdAt: -1 },
        $limit: DefaultPageLimit,
        $skip: this.products.length || 0
      };
      fetchProducts(this.$axios, {
        query: this.getProductsQueryWithFilter(query)
      })
        .then(({ data: newProducts, total: productsTotal }) => {
          this.products = filterProductsByRegion(
            [...this.products, ...newProducts],
            this.region
          );
          this.productsTotal = productsTotal;
        })
        .catch((err) => {
          this.$showToastMessage(
            this.$get(err, 'response.data.message'),
            'Error',
            "Can't fetch new products"
          );
        })
        .finally(() => (this.newProductsLoading = false));
    },
    handleFilterValueChange(value) {
      this.filterValue = value;
      this.filteredProductsLoading = true;
      const query = {
        $where: {
          formats: {
            $elemMatch: {
              'prices.region': this.region,
              deletedAt: { $exists: false },
              availability: ProductFormatAvailability.Available
            }
          }
        },
        $sort: { createdAt: -1 },
        $limit: DefaultPageLimit
      };
      fetchProducts(this.$axios, {
        query: this.getProductsQueryWithFilter(query)
      })
        .then(({ data: filteredProducts, total: productsTotal }) => {
          this.isNothingFound = isEmpty(filteredProducts);
          this.products = filterProductsByRegion(filteredProducts, this.region);
          this.productsTotal = productsTotal;
        })
        .catch((err) => {
          this.$showToastMessage(
            this.$get(err, 'response.data.message'),
            'Error',
            "Can't fetch products"
          );
        })
        .finally(() => (this.filteredProductsLoading = false));
    },
    getProductsQueryWithFilter(query) {
      if (this.filterValue !== 'all') {
        return {
          ...query,
          $where: {
            ...query.$where,
            name: { $regex: `^${this.filterValue}`, $options: 'i' }
          }
        };
      }
      return query;
    }
  }
};
</script>

<style lang="scss" scoped>
.products {
  &__top-banner {
    position: relative;
    display: flex;
    justify-content: flex-end;
    flex-direction: column;
    height: 255px;
    background-color: $primary-color;
    color: $dark-color;
    padding-left: 100px;
    padding-bottom: 35px;

    h1 {
      position: relative;
      font-family: 'PPRader-Bold', sans-serif;
      font-size: 40px;
      line-height: 46px;
      text-transform: uppercase;
    }

    @media (max-width: $screen-width-md) {
      padding-left: 15px;
      padding-bottom: 80px;
    }
  }

  &__title-background {
    background-color: $dark-color;
    border-radius: 16px;
    height: 48px;
    position: relative;
    top: -4px;

    @media (max-width: $screen-width-md) {
      position: absolute;
      width: 315px;
      line-height: 48px;
      top: auto;
      left: -25px;
      bottom: -46px;
      padding-left: 17px !important;
      border-top-left-radius: 0;
      border-bottom-left-radius: 0;
    }

    span {
      position: relative;
      top: 4px;
      color: $light-grey-color;
    }
  }
}

.mac-os {
  .products__title-background {
    span {
      top: 0;
    }
  }
}
</style>
